<template>
  <div class="stc-member-item">
    <div :id="containerId" class="stc-member-item__image-container">
      <div class="stc-member-item__image-wrapper" :data-product-id="productId">
        <!--
          Workaround for @atomic-ui Slideshow & Picture implementation:
          The library's Slideshow is a flex container that scales to the tallest item. Because
          the Picture component uses a default 0.8 aspect-ratio skeleton for lazy-loading,
          unloaded slides (e.g., slide #2) are often taller than the actual loaded image #1.
          This causes the Slideshow to expand, misaligning the vertically centered chevrons and toasts.

          The "Ghost Image" below establishes a stable baseline height based on the primary image
          in the document flow, while the actual gallery is positioned absolutely over it.
          This forces the Slideshow to respect the primary image's height and prevents alignment
          shifts during lazy-loading.
        -->
        <div class="stc-member-item__height-fixer" aria-hidden="true">
          <Picture :image-url="primaryImage" :alt="productImgAlt" :image-sizes-by-breakpoints="imageSizes" />
        </div>

        <div class="stc-member-item__slideshow-container">
          <div v-if="hasMultipleImages" class="product-link">
            <Slideshow :list="carouselImages" :show-indicator="false" autoplay-mode="manual">
              <template #item="{ data }">
                <Picture :image-url="data.path" :alt="productImgAlt" :image-sizes-by-breakpoints="imageSizes" :ai-image />
              </template>
            </Slideshow>
          </div>
          <a v-else :href="productUrl" class="product-link" target="_blank" :aria-label="`View details for ${productName}`">
            <Picture :image-url="primaryImage" :alt="productImgAlt" :image-sizes-by-breakpoints="imageSizes" :ai-image />
          </a>

          <Toast
            v-if="toastMessage"
            :toast-container-id="containerId"
            :duration="2000"
            placement="center"
            class="stc-member-item__toast"
            @destroy="toastMessage = ''"
          >
            {{ toastMessage }}
          </Toast>
        </div>
      </div>

      <AddToList v-if="isAddToListVisible" :container-id="containerId" is-overlay />
    </div>

    <div class="stc-member-item__order-panel">
      <div class="stc-member-item__available-details">
        <div class="stc-member-item__title-container">
          <ProductTitle tag="h4" :product-url="productUrl" allow-opening-in-new-tab @click="tagSTCMemberPDPClick" />
          <MarketPlaceTitle v-if="isMcomMarketplace" />
        </div>
        <Traits :product-id="productId" :product="product" />
        <LowAvailability :product-id="productId" class="stc-member-item__low-availability" />
        <span v-if="selectedPrice?.isGWPAvailable" class="stc-member-item__gwp-badge" data-testid="gwp-badge"> Gift with purchase </span>
        <Selector v-model="currentQuantity" :max="maxQuantity" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, provide } from 'vue';

import Picture from '@atomic-ui/picture/Picture';
import Slideshow from '@atomic-ui/slideshow/Slideshow';
import Toast from '@atomic-ui/toast/ToastRenderer';

import { useCreateTypeStore } from '@nuxt-common/media-card-large/app/modules/type';
import TypeParser from '@nuxt-common/media-card-large/app/modules/type/parsers/TypeParser';

import useProductStore from '~/stores/useProductStore';
import { useCollectionSelectionStore } from '~/stores/useCollectionSelectionStore';

import { useProductProvide } from '~/modules/product/composables/useProductProvide';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { useImagesProvide } from '~/composables/images/useImages';
import useUpc from '~/modules/upc/composables/useUpc';
import { useUpcValidator, useUpcValidatorProvide } from '~/modules/upc/composables/useUpcValidator';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductPriceProvide } from '~/modules/price/composables/useProductPriceProvide';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import { useBrand } from '~/composables/useBrand';
import { useMarketPlace } from '~/composables/useMarketPlace';
import { getLowestPrice } from '~/modules/price/parsers';
import { tagSTCSelectItem, tagSTCMemberPDPClick } from '~/features/analytics/shopTheCollection';

import ProductTitle from '~/components/productTitle/ProductTitle.common';
import MarketPlaceTitle from '~/components/marketPlace/MarketPlaceTitle';
import LowAvailability from '~/modules/availability/components/LowAvailability';
import AddToList from '~/modules/add-to-list/components/AddToList';
import Selector from './Selector.vue';
import Traits from './Traits.vue';
import { AI_WATERMARK_CONFIG } from '~/constants/common';

const props = defineProps<{
  productId: ProductId;
}>();

// Provide STC context for underlying components
provide('isSTC', true);

const { isMcom } = useBrand();

const selectionStore = useCollectionSelectionStore();
const memberProducts = useProductStore().getAllMembers;
const product = memberProducts[String(props.productId)];

// Initialize local context for member product
useProductProvide(props.productId, product);
useUpcValidatorProvide();
const { sellerId } = useMarketPlace();
useProductPriceProvide({ id: props.productId, product });

useCreateTypeStore({
  id: props.productId,
  data: TypeParser(product, { defaultToFirst: isMcom }),
});

const { marketPlaceItemEnabled, isAddToListVisible } = useFeatureEligibility();
const { isAiGeneratedMetaData } = useProductFlags();
const selectedPrice = useProductPrice();

const { primaryImageData, carouselImages } = useImagesProvide();
const { productUrl, productName } = useCommonProductGetters();
const { selectedUpcIdOrNull: selectedUpcId, selectedUpcNumber } = useUpc();
const { upcValidator } = useUpcValidator();

const toastMessage = ref('');

const imageSizes = {
  small: 350,
  medium: 350,
  large: 350,
  mlarge: 350,
  xlarge: 350,
};

const primaryImage = computed(() => primaryImageData.value?.path);
const productImgAlt = computed(() => `${productName} image`);
const hasMultipleImages = computed(() => carouselImages.value?.length > 1);
const upcValidationError = computed(() => upcValidator());
const maxQuantity = computed(() => product?.detail?.maxQuantity || 10);
const containerId = computed(() => `image-container-${props.productId}`);
const isMcomMarketplace = computed(() => marketPlaceItemEnabled && isMcom);

/**
 * Sync quantity between local state and the selection store.
 */
const currentQuantity = computed({
  get: () => {
    if (!selectedUpcId.value) return 0;
    return selectionStore.getQuantityForUpc(props.productId, selectedUpcId.value);
  },
  set: (val) => {
    if (upcValidationError.value) {
      toastMessage.value = upcValidationError.value;
      return;
    }

    if (!selectedUpcId.value) return;

    const priceFallback = product?.pricing?.price?.regularPrice?.values?.[0]?.value;
    const price = getLowestPrice(selectedPrice.value) || priceFallback;

    if (!price) {
      toastMessage.value = 'Price not available for this selection';
      return;
    }

    const offer = selectedPrice.value?.badgesList?.[0];
    const promoMessaging = offer?.promoId || 'no promo messaging';

    selectionStore.updateSelection(props.productId, selectedUpcId.value, {
      quantity: val,
      price,
      priceTypeId: selectedPrice.value?.priceTypeId || 'ZERO',
      promoMessaging,
      upcNumber: selectedUpcNumber.value,
      imageUrl: primaryImage.value || '',
      alt: `${productName} image`,
      sellerId: sellerId.value,
    });
  },
});

watch(currentQuantity, tagSTCSelectItem);

/**
 * Show toast when item is removed, but suppress it during bulk clearing.
 */
watch(
  () => selectionStore.getQuantityForProduct(props.productId),
  (newVal, oldVal) => {
    if (selectionStore.isSilentMode) return;
    if (oldVal > 0 && newVal === 0) {
      toastMessage.value = 'Item removed';
    }
  },
);

const aiImage = isAiGeneratedMetaData ? AI_WATERMARK_CONFIG.collectionDesktop : undefined;
</script>






<style lang="scss" scoped>
@import '~settingSCSS';

.stc-member-item {
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
  gap: 24px;
}

.stc-member-item__image-container {
  position: relative;
  flex: 0 1 342px;
  min-width: 0;
  align-self: flex-start;

  &:deep(.add-to-list-icon) {
    top: 8px;
    right: 8px;
  }
}

.stc-member-item__image-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.stc-member-item__height-fixer {
  visibility: hidden;
  pointer-events: none;
  width: 100%;
  height: 100%;

  :deep(.picture-container),
  :deep(picture) {
    width: 100%;
    height: 100%;
    display: block;
  }

  :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.stc-member-item__slideshow-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  .product-link {
    display: block;
    width: 100%;
    height: 100%;

    :deep(.v-slideshow),
    :deep(.v-slideshow-content),
    :deep(.v-slideshow-wrapper),
    :deep(.v-scroller),
    :deep(.v-slideshow-item) {
      height: 100%;
    }

    :deep(.picture-container),
    :deep(picture) {
      width: 100%;
      height: 100%;
      display: block;
    }

    :deep(img) {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}

.stc-member-item__order-panel {
  flex: 0 1 464px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.stc-member-item__available-details {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 24px;

  :deep(.credit-finance-container),
  :deep(.additional-fees) {
    display: none !important;
  }

  :deep(.secondary-promo) {
    @include body(regular);
  }

  .stc-member-item__title-container {
    :deep(.exclusive-text) {
      display: block;
      @include body(regular);
      @include margin-top-xxs;
      color: $gray-1-color;
    }
  }
}

.stc-member-item__low-availability {
  margin: 0 !important;
  margin-top: -12px !important;
}

.stc-member-item__toast {
  max-width: 90%;
  z-index: 2;
  background-color: rgba(0, 0, 0, 0.85);
}

.stc-member-item__gwp-badge {
  max-width: fit-content;
}


.stc-member-item__gwp-badge {
  padding: 2px 8px;
  background-color: $accent1-color;
}
</style>